import {
  useV2CreateTextBlockMutation,
  useV2UpdateTextBlockMutation,
  useV2DeleteTextBlockMutation,
} from "@generated";
import type { V2TextBlockResource } from "@generated";
import type { TextBlockFormValues } from "../types";

// Build the JSON:API `data` body for create/update. The `account` relationship
// required by V2TextBlockResource is inferred from auth server-side, so we omit
// it and cast (same pattern as the Currencies/BusinessUnits MSPAs). `slug` is
// derived server-side from `name` and is never written by the client.
export const buildTextBlockData = (
  values: TextBlockFormValues,
  id?: string
): V2TextBlockResource => {
  return {
    type: "text-blocks",
    ...(id ? { id } : {}),
    attributes: {
      name: values.name,
      "merge-block": values.mergeBlock,
    },
  } as unknown as V2TextBlockResource;
};

export const useTextBlockMutations = () => {
  const [create, createState] = useV2CreateTextBlockMutation();
  const [update, updateState] = useV2UpdateTextBlockMutation();
  const [destroy, destroyState] = useV2DeleteTextBlockMutation();

  return {
    create: (values: TextBlockFormValues) =>
      create({ body: { data: buildTextBlockData(values) } }).unwrap(),

    update: (id: string, values: TextBlockFormValues) =>
      update({
        id: id,
        body: { data: buildTextBlockData(values, id) },
      }).unwrap(),

    // Text blocks are NOT SoftDeletable — this is a hard delete.
    destroy: (id: string) => destroy({ id: id }).unwrap(),

    states: { createState, updateState, destroyState },
  };
};

export default useTextBlockMutations;
